import React, { useEffect, useState, useRef, Component } from 'react';
import { motion } from 'framer-motion';
import { MapPinIcon, PhoneIcon } from 'lucide-react';
import L from 'leaflet';
const cities = [{
  name: 'Shelton',
  lat: 47.215,
  lng: -123.1007
}, {
  name: 'Tacoma',
  lat: 47.2529,
  lng: -122.4443
}, {
  name: 'Lakewood',
  lat: 47.1718,
  lng: -122.5185
}, {
  name: 'Yelm',
  lat: 46.9418,
  lng: -122.6307
}, {
  name: 'Tenino',
  lat: 46.8554,
  lng: -122.8526
}, {
  name: 'Olympia',
  lat: 47.0379,
  lng: -122.9007
}, {
  name: 'Napavine',
  lat: 46.5732,
  lng: -122.9068
}, {
  name: 'Winlock',
  lat: 46.4904,
  lng: -122.9385
}, {
  name: 'Lacey',
  lat: 47.0343,
  lng: -122.8232
}, {
  name: 'Tumwater',
  lat: 46.971,
  lng: -122.9093
}, {
  name: 'Aberdeen',
  lat: 46.9754,
  lng: -123.8157
}, {
  name: 'Toledo',
  lat: 46.4393,
  lng: -122.8468
}];
const customIcon = L.divIcon({
  html: `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="42" viewBox="0 0 32 42">
    <path d="M16 0C7.163 0 0 7.163 0 16c0 12 16 26 16 26s16-14 16-26C32 7.163 24.837 0 16 0z" fill="#2E5A36" stroke="#C2A55E" stroke-width="2"/>
    <circle cx="16" cy="15" r="6" fill="#C2A55E"/>
  </svg>`,
  className: '',
  iconSize: [32, 42],
  iconAnchor: [16, 42],
  popupAnchor: [0, -42]
});
export function ServiceAreas() {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const [activeCity, setActiveCity] = useState<string | null>(null);
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;
    const map = L.map(mapRef.current, {
      scrollWheelZoom: false,
      zoomControl: true
    }).setView([46.95, -122.85], 8);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
    }).addTo(map);
    cities.forEach(city => {
      const marker = L.marker([city.lat, city.lng], {
        icon: customIcon
      }).addTo(map);
      marker.bindPopup(`<div style="text-align:center;font-family:Inter,sans-serif;padding:4px 2px;">
          <strong style="font-size:14px;color:#fff;">${city.name}</strong>
          <br/><span style="font-size:12px;color:#C2A55E;">Service Area</span>
        </div>`, {
        className: 'custom-popup'
      });
      marker.on('mouseover', () => {
        marker.openPopup();
        setActiveCity(city.name);
      });
      marker.on('mouseout', () => {
        marker.closePopup();
        setActiveCity(null);
      });
      marker.on('click', () => {
        marker.openPopup();
        setActiveCity(city.name);
      });
    });
    // Draw a polygon connecting the outer service area
    const bounds = cities.map(c => [c.lat, c.lng] as [number, number]);
    L.polygon(bounds, {
      color: '#C2A55E',
      weight: 2,
      fillColor: '#1A3621',
      fillOpacity: 0.08,
      dashArray: '6 4'
    }).addTo(map);
    mapInstanceRef.current = map;
    return () => {
      map.remove();
      mapInstanceRef.current = null;
    };
  }, []);
  const handleCityClick = (cityName: string) => {
    const city = cities.find(c => c.name === cityName);
    if (city && mapInstanceRef.current) {
      mapInstanceRef.current.flyTo([city.lat, city.lng], 11, {
        duration: 1
      });
      setActiveCity(cityName);
    }
  };
  return <section id="areas" className="py-24 bg-white" data-id="element-197">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-198">
        <div className="text-center mb-12" data-id="element-199">
          <h2 className="text-3xl md:text-4xl font-bold text-forest mb-4" data-id="element-200">
            Service Areas
          </h2>
          <div className="w-24 h-1 bg-gold mx-auto rounded-full mb-8" data-id="element-201"></div>
          <p className="text-xl text-gray-600 font-medium" data-id="element-202">
            Proudly serving Residential and Commercial clients in:
          </p>
        </div>

        {/* Map */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="mb-12 rounded-2xl overflow-hidden shadow-lg border border-gray-200" data-id="element-203">
          <div ref={mapRef} className="w-full h-[400px] md:h-[500px]" data-id="element-204" />
        </motion.div>

        {/* City Badges */}
        <div className="flex flex-wrap justify-center gap-4 max-w-5xl mx-auto" data-id="element-205">
          {cities.map((city, index) => <motion.button key={city.name} initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.3,
          delay: index * 0.05
        }} onClick={() => handleCityClick(city.name)} className={`flex items-center gap-2 px-6 py-3 rounded-full border shadow-sm transition-all cursor-pointer ${activeCity === city.name ? 'bg-forest text-white border-forest' : 'bg-tan border-tan-dark hover:border-gold hover:bg-white'}`} data-id="element-206">
              <MapPinIcon className={`w-5 h-5 ${activeCity === city.name ? 'text-gold' : 'text-forest'}`} data-id="element-207" />
              <span className="font-semibold" data-id="element-208">{city.name}</span>
            </motion.button>)}
        </div>

        {/* Not in the list CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.4
      }} className="mt-12 text-center" data-id="element-209">
          <p className="text-gray-600 text-lg mb-4 font-light" data-id="element-210">
            Don't see your city listed?{' '}
            <span className="font-semibold text-forest" data-id="element-211">
              We may still serve your area!
            </span>
          </p>
          <a href="tel:360-623-4531" className="inline-flex items-center gap-3 bg-forest hover:bg-forest-light text-white px-8 py-4 rounded-xl font-bold text-lg transition-all duration-300 shadow-lg hover:shadow-xl" data-id="element-212">
            <PhoneIcon className="w-5 h-5 text-gold" data-id="element-213" />
            Call Us — 360-623-4531
          </a>
        </motion.div>
      </div>
    </section>;
}